import { EnvioType } from '_metronic/helpers/enums'
import type { PrescricaoDtoDadosPaciente } from '../_model'

export type CanalEnvio = 'whatsapp' | 'sms' | 'email'

export const CANAIS_ENVIO: CanalEnvio[] = ['whatsapp', 'sms', 'email']

/** Como o médico quer mandar os documentos ao paciente depois de assinar. */
export type PreferenciasEnvio = {
  whatsapp: boolean
  sms: boolean
  email: boolean
  telefone: string
  enderecoEmail: string
}

const TELEFONE = /^\(\d{2}\) (\d )?\d{4}-\d{4}$/
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const telefoneValido = (telefone: string) => TELEFONE.test(telefone.trim())
export const emailValido = (email: string) => EMAIL.test(email.trim())

/**
 * Telefone do cadastro no formato que o envio exige — `(00) 0 0000-0000` ou `(00) 0000-0000` —,
 * venha ele só com dígitos ou já formatado. Sem DDD completo, devolve como está.
 */
export const formatarTelefone = (telefone?: string | null) => {
  const digitos = (telefone ?? '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '')
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos[2]} ${digitos.slice(3, 7)}-${digitos.slice(7)}`
  }
  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`
  }
  return telefone ?? ''
}

export const canaisMarcados = (preferencias: PreferenciasEnvio): CanalEnvio[] =>
  CANAIS_ENVIO.filter((canal) => preferencias[canal])

/** O que ainda impede o envio pelos canais marcados. */
export const pendenciasDeEnvio = (preferencias: PreferenciasEnvio): ('telefone' | 'email')[] => {
  const faltam: ('telefone' | 'email')[] = []
  if ((preferencias.whatsapp || preferencias.sms) && !telefoneValido(preferencias.telefone)) {
    faltam.push('telefone')
  }
  if (preferencias.email && !emailValido(preferencias.enderecoEmail)) faltam.push('email')
  return faltam
}

/**
 * Corpo de `POST /prescricao/envia/{id}` para os canais pedidos. O backend manda por telefone
 * quando há telefone (`tipoEnvio` escolhe WhatsApp, SMS ou os dois) e por e-mail quando há
 * e-mail, então o canal desmarcado vai vazio. `null` quando não há nada a enviar.
 */
export const dadosDeEnvio = (
  preferencias: PreferenciasEnvio,
  canais: CanalEnvio[]
): PrescricaoDtoDadosPaciente | null => {
  const whatsapp = canais.includes('whatsapp')
  const sms = canais.includes('sms')
  const email = canais.includes('email')
  const tipoEnvio =
    whatsapp && sms ? EnvioType.Ambos : whatsapp ? EnvioType.Whatsapp : sms ? EnvioType.Sms : null
  if (!tipoEnvio && !email) return null
  return {
    telefone: tipoEnvio ? preferencias.telefone.trim() : null,
    // Sem telefone o backend ignora o tipo; ele vai preenchido só porque o DTO o declara.
    tipoEnvio: tipoEnvio ?? EnvioType.Whatsapp,
    email: email ? preferencias.enderecoEmail.trim() : null,
  }
}
